import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map, Observable, of } from 'rxjs';
import { CodedValueItem } from '../interfaces/coded-value-item.interface';
import { EsriUtils } from '../utils/esri.utils';
import { CacheFactoryService, ICacheService } from './cache-factory.service';

const districtLabelField = 'distrito';
const districtValueField = 'di';
const countyLabelField = 'concelho';
const countyValueField = 'dico';
const parishLabelField = 'freguesia';
const parishValueField = 'dicofre';

function handleLocalizationFilters(
  filters: Array<{ field: string; value: string }>
) {
  return filters
    .map((x) => {
      return `lower(${x.field})='${x.value}'`;
    })
    .join(' and ');
}

@Injectable()
export class MapDomainService {
  private cacheService: ICacheService;

  constructor(private http: HttpClient, cacheFactory: CacheFactoryService) {
    this.cacheService = cacheFactory.getOrCreate('map-domains');
  }

  getDistrictValueByLabel(
    districtLabel: string
  ): Observable<CodedValueItem<string>> {
    districtLabel = districtLabel?.toLowerCase();
    return this.cacheService.getCached({
      path: `districtList/ByLabel/${districtLabel}`,
      source: () => {
        return this.getDistrictDomainList().pipe(
          map((list) =>
            list?.find((x) => x.label?.toLowerCase() === districtLabel)
          )
        );
      },
    });
  }

  getDistrictGeometryByLabel(
    districtLabel: string
  ): Observable<__esri.Geometry> {
    districtLabel = districtLabel?.toLowerCase();
    return this.cacheService.getCached({
      path: `districtList/ByLabel/${districtLabel}/geometry`,
      source: () => {
        const params = new HttpParams({
          fromObject: {
            f: 'json',
            returnGeometry: true,
            outFields: '',
            resultRecordCount: 1,
            where: handleLocalizationFilters([
              {
                field: districtLabelField,
                value: districtLabel,
              },
            ]),
          },
        });

        return this.http
          .get<any>(`~administrativeRegionsService/1/query`, {
            params,
          })
          .pipe(mapFeaturesResToGeometryOperator());
      },
    });
  }

  getDistrictDomainList(): Observable<CodedValueItem<string>[]> {
    return this.cacheService.getCached({
      path: `districtList`,
      source: () => {
        const params = new HttpParams({
          fromObject: {
            f: 'json',
            returnGeometry: false,
            returnDistinctValues: true,
            outFields: [districtLabelField, districtValueField].join(','),
            orderByFields: [districtLabelField].join(','),
            where: '1=1',
          },
        });

        return this.http
          .get<any>(`~administrativeRegionsService/1/query`, { params })
          .pipe(
            mapFeaturesResToValueItemsOperator(
              districtValueField,
              districtLabelField
            )
          );
      },
    });
  }

  getCountyValueByLabel(
    district: string,
    countyLabel: string
  ): Observable<CodedValueItem<string>> {
    countyLabel = countyLabel?.toLowerCase();
    return this.cacheService.getCached({
      path: `districtList/${district}/countyList/ByLabel/${countyLabel}`,
      source: () => {
        return this.getCountyDomainList(district).pipe(
          map((list) =>
            list?.find((x) => x.label?.toLowerCase() === countyLabel)
          )
        );
      },
    });
  }

  getCountyGeometryByLabel(
    districtLabel: string,
    countyLabel: string
  ): Observable<__esri.Geometry> {
    districtLabel = districtLabel?.toLowerCase();
    countyLabel = countyLabel?.toLowerCase();
    return this.cacheService.getCached({
      path: `districtList/${districtLabel}/countyList/ByLabel/${countyLabel}/geometry`,
      source: () => {
        const params = new HttpParams({
          fromObject: {
            f: 'json',
            returnGeometry: true,
            outFields: '',
            resultRecordCount: 1,
            where: handleLocalizationFilters([
              {
                field: districtLabelField,
                value: districtLabel,
              },
              {
                field: countyLabelField,
                value: countyLabel,
              },
            ]),
          },
        });

        return this.http
          .get<any>(`~administrativeRegionsService/0/query`, {
            params,
          })
          .pipe(mapFeaturesResToGeometryOperator());
      },
    });
  }

  getCountyDomainList(district: string): Observable<CodedValueItem<string>[]> {
    if (!district) {
      return of([]);
    }

    return this.cacheService.getCached({
      path: `districtList/${district}/countyList`,
      source: () => {
        const params = new HttpParams({
          fromObject: {
            f: 'json',
            returnGeometry: false,
            returnDistinctValues: true,
            outFields: [countyLabelField, countyValueField].join(','),
            orderByFields: [countyLabelField].join(','),
            where: `${districtValueField}='${district}'`,
          },
        });

        return this.http
          .get<any>(`~administrativeRegionsService/0/query`, { params })
          .pipe(
            mapFeaturesResToValueItemsOperator(
              countyValueField,
              countyLabelField
            )
          );
      },
    });
  }

  getParishValueByLabel(
    county: string,
    parishLabel: string
  ): Observable<CodedValueItem<string>> {
    parishLabel = parishLabel?.toLowerCase();
    return this.cacheService.getCached({
      path: `countyList/${county}/parishList/ByLabel/${parishLabel}`,
      source: () => {
        return this.getParishDomainList(county).pipe(
          map((list) =>
            list?.find((x) => x.label?.toLowerCase() === parishLabel)
          )
        );
      },
    });
  }

  getParishGeometryByLabel(
    districtLabel: string,
    countyLabel: string,
    parishLabel: string
  ): Observable<__esri.Geometry> {
    districtLabel = districtLabel?.toLowerCase();
    countyLabel = countyLabel?.toLowerCase();
    parishLabel = parishLabel?.toLowerCase();
    return this.cacheService.getCached({
      path: `districtList/${districtLabel}/countyList/${countyLabel}/parishList/ByLabel/${parishLabel}/geometry`,
      source: () => {
        const params = new HttpParams({
          fromObject: {
            f: 'json',
            returnGeometry: true,
            outFields: '',
            resultRecordCount: 1,
            where: handleLocalizationFilters([
              {
                field: districtLabelField,
                value: districtLabel,
              },
              {
                field: countyLabelField,
                value: countyLabel,
              },
              {
                field: parishLabelField,
                value: parishLabel,
              },
            ]),
          },
        });

        return this.http
          .get<any>(`~administrativeRegionsService/2/query`, {
            params,
          })
          .pipe(mapFeaturesResToGeometryOperator());
      },
    });
  }

  getParishDomainList(county: string): Observable<CodedValueItem<string>[]> {
    if (!county) {
      return of([]);
    }

    return this.cacheService.getCached({
      path: `countyList/${county}/parishList`,
      source: () => {
        const params = new HttpParams({
          fromObject: {
            f: 'json',
            returnGeometry: false,
            returnDistinctValues: true,
            outFields: [parishLabelField, parishValueField].join(','),
            orderByFields: [parishLabelField].join(','),
            where: `${countyValueField}='${county}'`,
          },
        });

        return this.http
          .get<any>(`~administrativeRegionsService/2/query`, { params })
          .pipe(
            mapFeaturesResToValueItemsOperator(
              parishValueField,
              parishLabelField
            )
          );
      },
    });
  }

  getDomainListByField(fieldName: string) {
    return this.getDomainListMapped().pipe(map((x) => x.get(fieldName)));
  }

  private getDomainListMapped(): Observable<
    Map<string, CodedValueItem<any>[]>
  > {
    return this.cacheService.getCached({
      path: `domainListMapped`,
      source: () => {
        return this.http.get<any>(`~registerObjectsService/1?f=json`).pipe(
          map((res) => {
            const collection = new Map<string, CodedValueItem<any>[]>();

            res?.fields?.forEach((field) => {
              const codedValues = (field?.domain?.codedValues as any[])?.map<
                CodedValueItem<any>
              >((x) => {
                return {
                  label: x.name,
                  value: x.code,
                };
              });
              if (codedValues) {
                collection.set(field.name, codedValues);
              }
            });

            return collection;
          })
        );
      },
    });
  }
}

function mapFeaturesResToValueItemsOperator(
  valueField: string,
  labelField: string
) {
  return map<any, CodedValueItem<string>[]>((res) => {
    return (
      (res?.features as Array<any>)?.map<CodedValueItem<string>>((x) => {
        return {
          value: x.attributes[valueField],
          label: x.attributes[labelField],
        };
      }) ?? []
    );
  });
}

function mapFeaturesResToGeometryOperator() {
  return map<any, __esri.Geometry>((res) => {
    const geomJson = res?.features?.[0]?.geometry;
    const spatialReference = res?.spatialReference;
    if (geomJson && spatialReference) {
      geomJson.spatialReference = spatialReference;
    }

    return EsriUtils.geometryFromJson(geomJson);
  });
}
